"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const class_service_consts_1 = require("@seneca/class-service-consts");
const client_library_1 = require("@seneca/client-library");
const AllClassDeletionsDidNotSucceedError_1 = __importDefault(require("./errors/AllClassDeletionsDidNotSucceedError"));
const ClassBodyNotValidError_1 = __importDefault(require("./errors/ClassBodyNotValidError"));
const ClassDoesNotExistError_1 = __importDefault(require("./errors/ClassDoesNotExistError"));
const ClassEnrollmentBodyNotValidError_1 = __importDefault(require("./errors/ClassEnrollmentBodyNotValidError"));
const ClassEnrollmentDoesNotExistError_1 = __importDefault(require("./errors/ClassEnrollmentDoesNotExistError"));
const ClassSizeLimitExceeded_1 = __importDefault(require("./errors/ClassSizeLimitExceeded"));
const ClassTeacherLimitExceeded_1 = __importDefault(require("./errors/ClassTeacherLimitExceeded"));
const ForbiddenAccessToChildEnrollments_1 = __importDefault(require("./errors/ForbiddenAccessToChildEnrollments"));
const ForbiddenForManagedClass_1 = __importDefault(require("./errors/ForbiddenForManagedClass"));
const InvalidClassIdsParameter_1 = __importDefault(require("./errors/InvalidClassIdsParameter"));
const InvalidLastKeyParameter_1 = __importDefault(require("./errors/InvalidLastKeyParameter"));
const SendParentInviteBadRequest_1 = __importDefault(require("./errors/SendParentInviteBadRequest"));
const SendParentInviteInvalidRequest_1 = __importDefault(require("./errors/SendParentInviteInvalidRequest"));
const StudentParentConnectionNotFoundInClass_1 = __importDefault(require("./errors/StudentParentConnectionNotFoundInClass"));
const TeacherNotInClassesError_1 = __importDefault(require("./errors/TeacherNotInClassesError"));
const UserHasReachedMaximumNumberOfActiveClasses_1 = __importDefault(require("./errors/UserHasReachedMaximumNumberOfActiveClasses"));
const UserHasReachedMaximumNumberOfClassCourses_1 = __importDefault(require("./errors/UserHasReachedMaximumNumberOfClassCourses"));
const UserHasReachedMaximumNumberOfEnroledClasses_1 = __importDefault(require("./errors/UserHasReachedMaximumNumberOfEnroledClasses"));
const FetchClassesSectionStatsBadRequestError_1 = __importDefault(require("./errors/FetchClassesSectionStatsBadRequestError"));
const UserIsNotClassTeacherError_1 = __importDefault(require("./errors/UserIsNotClassTeacherError"));
const NotFoundError_1 = __importDefault(require("./errors/NotFoundError"));
class ClassServiceClient extends client_library_1.Client {
    constructor(fetch, options) {
        super(fetch, options);
    }
    async createClass(klass) {
        this._validateMissingParams({ class: klass });
        const url = `${this.url}/api/classes/me`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify(klass) }, [201, 400, 422]);
        if (response.status === 400) {
            const body = response.json;
            if (body.reason === class_service_consts_1.errorCodes.MAX_ACTIVE_CLASSES_REACHED) {
                throw new UserHasReachedMaximumNumberOfActiveClasses_1.default();
            }
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassBodyNotValidError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async fetchClass(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/classes/me/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 404]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        return response.json;
    }
    async fetchAllClasses({ schoolId } = {}) {
        let classes = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfClasses({
                lastKey,
                schoolId
            });
            classes = classes.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return classes;
    }
    async archiveClassesAdmin({ classIds, archive }) {
        this._validateMissingParams({
            classIds,
            archive
        });
        const url = `${this.url}/api/admin/classes/archive`;
        const response = await this.requestMaker.makeRequest(url, { method: 'PUT', body: JSON.stringify({ classIds, archive }) }, [200]);
        return response.json;
    }
    async fetchClassesAdmin(classIds) {
        let url = `${this.url}/api/admin/classes`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                classIds
            })
        }, [200]);
        return response.json;
    }
    async fetchClassesBareAdmin(classIds) {
        let url = `${this.url}/api/admin/classes/bare`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                classIds
            })
        }, [200]);
        return response.json;
    }
    async fetchClassesForUserAdmin({ userId, schoolId }) {
        let url = `${this.url}/api/admin/classes/user/${userId}`;
        if (schoolId) {
            url += `?schoolId=${schoolId}`;
        }
        const response = await this.requestMaker.makeRequest(url, {
            method: 'GET'
        }, [200]);
        return response.json;
    }
    async addTeacherAdmin(classId, userId) {
        let url = `${this.url}/api/admin/teacher`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                classId,
                userId
            })
        }, [200]);
        return response.json;
    }
    async fetchPageOfClasses(options) {
        let url = `${this.url}/api/classes/me`;
        const params = this._generatePagingQueryParams(options);
        if (options.schoolId) {
            params.push(`schoolId=${options.schoolId}`);
        }
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200]);
        return response.json;
    }
    async fetchAllClassSkeletons({ schoolId } = {}) {
        let skeletons = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfClassSkeletons({
                lastKey,
                schoolId
            });
            skeletons = skeletons.concat(page.items);
            lastKey = page.lastKey;
        } while (lastKey);
        return skeletons;
    }
    async fetchPageOfClassSkeletons(options) {
        let url = `${this.url}/api/classes/skeletons/me`;
        const params = this._generatePagingQueryParams(options);
        if (options.schoolId) {
            params.push(`schoolId=${options.schoolId}`);
        }
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200]);
        return response.json;
    }
    async updateClass(klass, classId) {
        this._validateMissingParams({ class: klass, classId });
        const url = `${this.url}/api/classes/me/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'PATCH', body: JSON.stringify(klass) }, [200, 400, 403, 404, 422]);
        if (response.status === 400) {
            const body = response.json;
            if (body.reason === class_service_consts_1.errorCodes.MAX_ACTIVE_CLASSES_REACHED) {
                throw new UserHasReachedMaximumNumberOfActiveClasses_1.default();
            }
            if (body.reason === class_service_consts_1.errorCodes.MAX_COURSES_FOR_CLASS_REACHED) {
                throw new UserHasReachedMaximumNumberOfClassCourses_1.default();
            }
        }
        if (response.status === 403) {
            const { reason } = response.json;
            if (reason === class_service_consts_1.errorCodes.FORBIDDEN_FOR_MANAGED_CLASS) {
                throw new ForbiddenForManagedClass_1.default(classId);
            }
        }
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassBodyNotValidError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async deleteClass(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/classes/me/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE' }, [204, 403, 404]);
        if (response.status === 403) {
            const { reason } = response.json;
            if (reason === class_service_consts_1.errorCodes.FORBIDDEN_FOR_MANAGED_CLASS) {
                throw new ForbiddenForManagedClass_1.default(classId);
            }
        }
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
    }
    async leaveClass(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/classes/${classId}/teachers/me`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE' }, [204, 404]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
    }
    async batchDeleteStudents(classId, userIds) {
        this._validateMissingParams({ classId, userIds });
        const url = `${this.url}/api/classes/me/${classId}/students/batch`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE', body: JSON.stringify({ userIds }) }, [
            200,
            403,
            404
        ]);
        if (response.status === 403) {
            const { reason } = response.json;
            if (reason === class_service_consts_1.errorCodes.FORBIDDEN_FOR_MANAGED_CLASS) {
                throw new ForbiddenForManagedClass_1.default(classId);
            }
        }
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        return response.json;
    }
    async batchDeleteTeachers(classId, userIds) {
        this._validateMissingParams({ classId, userIds });
        const url = `${this.url}/api/classes/me/${classId}/teachers/batch`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE', body: JSON.stringify({ userIds }) }, [
            200,
            404,
            422
        ]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassBodyNotValidError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async batchDeleteClasses(classIds) {
        this._validateMissingParams({ classIds });
        const url = `${this.url}/api/admin/classes/batch-delete`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE', body: JSON.stringify({ classIds }) }, [200, 422, 500]);
        if (response.status === 422) {
            throw new InvalidClassIdsParameter_1.default();
        }
        if (response.status === 500) {
            const { failedClassDeletions } = response.json;
            throw new AllClassDeletionsDidNotSucceedError_1.default(failedClassDeletions);
        }
        return response.json;
    }
    async createTutorTuteeClass(tutorId, tuteeId) {
        this._validateMissingParams({ tutorId, tuteeId });
        const url = `${this.url}/api/admin/create-tutor-tutee-class`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ tutorId, tuteeId })
        }, [200, 404]);
        if (response.status === 404) {
            const reason = response.json;
            throw new NotFoundError_1.default(reason);
        }
        return response.json;
    }
    async fetchUsersClassEnrollments({ schoolId } = {}) {
        let enrollments = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfUsersClassEnrollments({
                lastKey,
                schoolId
            });
            lastKey = page.lastKey;
            enrollments = enrollments.concat(page.items);
        } while (lastKey);
        return enrollments;
    }
    async fetchPageOfUsersClassEnrollments(options) {
        let url = `${this.url}/api/enrollments/me/classes`;
        const params = this._generatePagingQueryParams(options);
        if (options.schoolId) {
            params.push(`schoolId=${options.schoolId}`);
        }
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200]);
        return response.json;
    }
    async fetchUsersClassEnrollment(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/enrollments/me/classes/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 404]);
        if (response.status === 404) {
            throw new ClassEnrollmentDoesNotExistError_1.default(classId);
        }
        return response.json;
    }
    async enrollUserInClass(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/enrollments/me/classes/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'PUT', body: JSON.stringify({ classId }) }, [204, 400, 403, 422]);
        if (response.status === 400) {
            const { reason } = response.json;
            if (reason === 'Class has exceeded the limit for the number of enrolees') {
                throw new ClassSizeLimitExceeded_1.default(classId);
            }
            if (reason ===
                'User has exceeded the limit for the number of enroled classes') {
                throw new UserHasReachedMaximumNumberOfEnroledClasses_1.default(reason);
            }
        }
        if (response.status === 403) {
            const { reason, meta } = response.json;
            if (reason === class_service_consts_1.errorCodes.FORBIDDEN_FOR_MANAGED_CLASS) {
                throw new ForbiddenForManagedClass_1.default(classId, meta);
            }
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassEnrollmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
    }
    async unenrollUserFromClass(classId) {
        const url = `${this.url}/api/enrollments/me/classes/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE' }, [204, 403, 422]);
        if (response.status === 403) {
            const { reason } = response.json;
            if (reason === class_service_consts_1.errorCodes.FORBIDDEN_FOR_MANAGED_CLASS) {
                throw new ForbiddenForManagedClass_1.default(classId);
            }
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassEnrollmentBodyNotValidError_1.default(body.pointer, body.reason);
        }
    }
    async inviteTeacher(classId, email) {
        this._validateMissingParams({ classId, email });
        const url = `${this.url}/api/invites/classes/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ email }) }, [200, 400, 404, 422]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        if (response.status === 400) {
            const { reason } = response.json;
            if (reason === 'Class has exceeded the limit for the number of teachers') {
                throw new ClassTeacherLimitExceeded_1.default(classId);
            }
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassBodyNotValidError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async acceptInvite(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/invites/classes/${classId}/accept`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST' }, [200, 404]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        return response.json;
    }
    async rejectInvite(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/invites/classes/${classId}/reject`;
        const response = await this.requestMaker.makeRequest(url, { method: 'DELETE' }, [204, 404]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
    }
    async fetchInvite(classId) {
        this._validateMissingParams({ classId });
        const url = `${this.url}/api/invites/classes/${classId}`;
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 404]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        return response.json;
    }
    async transferClassOwnership(classId, userId) {
        this._validateMissingParams({ classId, userId });
        const url = `${this.url}/api/classes/me/${classId}/transfer`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ userId }) }, [200, 404]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        return response.json;
    }
    async archiveClasses({ classIds, archive }) {
        this._validateMissingParams({
            classIds,
            archive
        });
        const url = `${this.url}/api/classes/archive/me`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ classIds, archive }) }, [200, 403]);
        if (response.status === 403) {
            throw new UserIsNotClassTeacherError_1.default();
        }
        return response.json;
    }
    async hideClasses({ classIds, hide }) {
        this._validateMissingParams({
            classIds,
            hide
        });
        const url = `${this.url}/api/classes/hide/me`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ classIds, hide }) }, [200, 403]);
        if (response.status === 403) {
            throw new UserIsNotClassTeacherError_1.default();
        }
        return response.json;
    }
    async fetchChildClassEnrollments(childUserId) {
        let enrollments = [];
        let lastKey;
        do {
            const page = await this.fetchPageOfChildClassEnrollments(childUserId, {
                lastKey
            });
            lastKey = page.lastKey;
            enrollments = enrollments.concat(page.items);
        } while (lastKey);
        return enrollments;
    }
    async fetchPageOfChildClassEnrollments(childUserId, options) {
        if (!options.limit)
            options.limit = 100;
        let url = `${this.url}/api/parent/child/${childUserId}/enrollments/classes`;
        const params = this._generatePagingQueryParams(options);
        if (params.length !== 0) {
            url += `?${params.join('&')}`;
        }
        const response = await this.requestMaker.makeRequest(url, { method: 'GET' }, [200, 400, 403]);
        if (response.status === 400) {
            throw new InvalidLastKeyParameter_1.default();
        }
        if (response.status === 403) {
            throw new ForbiddenAccessToChildEnrollments_1.default(childUserId);
        }
        return response.json;
    }
    async sendParentInvite(body) {
        var _a;
        const url = `${this.url}/api/teacher/send-parent-invite`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify(body) }, [200, 400, 403, 404, 422]);
        if (response.status === 403) {
            throw new UserIsNotClassTeacherError_1.default();
        }
        if (response.status === 404) {
            throw new StudentParentConnectionNotFoundInClass_1.default();
        }
        if (response.status === 400) {
            throw new SendParentInviteBadRequest_1.default((_a = response.json) === null || _a === void 0 ? void 0 : _a.reason);
        }
        if (response.status === 422) {
            throw new SendParentInviteInvalidRequest_1.default();
        }
    }
    async batchSendParentInvites(body) {
        var _a;
        const url = `${this.url}/api/teacher/send-parent-invite/batch`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify(body) }, [200, 400, 403, 422]);
        if (response.status === 403) {
            throw new UserIsNotClassTeacherError_1.default();
        }
        if (response.status === 400) {
            throw new SendParentInviteBadRequest_1.default((_a = response.json) === null || _a === void 0 ? void 0 : _a.reason);
        }
        if (response.status === 422) {
            throw new SendParentInviteInvalidRequest_1.default();
        }
    }
    async teacherClassesSendParentInvites({ classIds, inviteRequestSource } = {}) {
        const url = `${this.url}/api/teacher/classes/send-parent-invite/batch`;
        const options = Object.assign({ method: 'POST' }, ((classIds === null || classIds === void 0 ? void 0 : classIds.length) && {
            body: JSON.stringify({ classIds, inviteRequestSource })
        }));
        const response = await this.requestMaker.makeRequest(url, options, [200, 400, 403]);
        if (response.status === 403) {
            throw new TeacherNotInClassesError_1.default();
        }
        if (response.status === 400) {
            const body = response.json;
            throw new SendParentInviteBadRequest_1.default(body.reason);
        }
        return response.json;
    }
    async updateClassLastViewed(classId, lastViewed) {
        const url = `${this.url}/api/enrollments/me/classes/${classId}/last-viewed`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify({ lastViewed }) }, [
            200,
            404,
            422
        ]);
        if (response.status === 404) {
            throw new ClassDoesNotExistError_1.default(classId);
        }
        if (response.status === 422) {
            const body = response.json;
            throw new ClassBodyNotValidError_1.default(body.pointer, body.reason);
        }
        return response.json;
    }
    async requestInviteTeacherClassesParents({ userId, classIds, inviteRequestSource }) {
        let url = `${this.url}/api/admin/teacher/request-invite-classes-parents`;
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({
                userId,
                classIds,
                inviteRequestSource
            })
        }, [204]);
        return response.json;
    }
    async setRevokedStatus({ classIds, revoke }) {
        this._validateMissingParams({
            classIds,
            revoke
        });
        const url = `${this.url}/api/admin/classes/revoke`;
        const response = await this.requestMaker.makeRequest(url, { method: 'PUT', body: JSON.stringify({ classIds, revoke }) }, [200]);
        return response.json;
    }
    async fetchClassesSectionStats(params) {
        const url = `${this.url}/api/classes/section-stats`;
        const response = await this.requestMaker.makeRequest(url, { method: 'POST', body: JSON.stringify(params) }, [200, 400, 403]);
        if (response.status === 403) {
            throw new TeacherNotInClassesError_1.default();
        }
        if (response.status === 400) {
            const body = response.json;
            throw new FetchClassesSectionStatsBadRequestError_1.default(body.reason);
        }
        return response.json;
    }
}
exports.default = ClassServiceClient;
//# sourceMappingURL=ClassServiceClient.js.map